import { useState } from "react";
import { format, isPast, isToday, differenceInDays } from "date-fns";
import { CalendarClock, AlertCircle, CalendarPlus, Loader2, X } from "lucide-react";

import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { SidebarHighlightCard } from "./SidebarHighlightCard";

interface TarefaDataPrazoCardProps {
  dataPrazo?: Date | string | null;
  isFechada: boolean;
  canEdit: boolean;
  isLoading: boolean;
  onUpdate: (data: Date | null) => void;
}

export function TarefaDataPrazoCard({
  dataPrazo,
  isFechada,
  canEdit,
  isLoading,
  onUpdate,
}: TarefaDataPrazoCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (date: Date | undefined) => {
    onUpdate(date || null);
    setIsOpen(false);
  };

  const hasPrazo = !!dataPrazo;

  // 1. Se não tem prazo e não pode editar: não mostra nada.
  if (!hasPrazo && !canEdit) return null;

  // 2. Se não tem prazo e PODE editar: botão pontilhado para adicionar
  if (!hasPrazo && canEdit) {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger
          className={buttonVariants({
            variant: "outline",
            className:
              "h-auto w-full border-dashed border-border/60 bg-transparent py-3 text-muted-foreground transition-all hover:bg-muted/50 hover:text-foreground",
          })}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <CalendarPlus className="mr-2 h-4 w-4" />
          )}
          Adicionar Prazo
        </PopoverTrigger>
        <PopoverContent className="pointer-events-auto w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={undefined}
            onSelect={handleSelect}
            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))} // MinDate = hoje
            initialFocus
          />
        </PopoverContent>
      </Popover>
    );
  }

  // 3. Se TEM prazo, calculamos o visual (Atrasado, No Prazo, etc)
  const prazo = new Date(dataPrazo!);
  const hoje = new Date();

  let alertConfig = {
    color: "text-primary",
    bg: "bg-primary/5 border-primary/20",
    label: "No Prazo",
    icon: CalendarClock,
  };

  if (isFechada) {
    alertConfig = {
      color: "text-muted-foreground",
      bg: "bg-muted/10 border-border/30",
      label: "Encerrado",
      icon: CalendarClock,
    };
  } else if (isPast(prazo) && !isToday(prazo)) {
    alertConfig = {
      color: "text-destructive",
      bg: "bg-destructive/10 border-destructive/20",
      label: "Atrasado",
      icon: AlertCircle,
    };
  } else if (isToday(prazo) || differenceInDays(prazo, hoje) <= 2) {
    alertConfig = {
      color: "text-amber-600 dark:text-amber-500",
      bg: "bg-amber-500/10 border-amber-500/20",
      label: "Vencendo em breve",
      icon: CalendarClock,
    };
  }

  const PrazoCardUI = (
    <SidebarHighlightCard
      icon={alertConfig.icon}
      title="Prazo"
      tag={alertConfig.label}
      value={format(prazo, "dd/MM/yyyy")}
      description={describePrazo(prazo, hoje, isFechada)}
      color={alertConfig.color}
      bg={alertConfig.bg}
      valueClassName={alertConfig.color}
      editable={canEdit}
      isLoading={isLoading}
    />
  );

  // Se não pode editar, renderiza só a UI estática
  if (!canEdit) return PrazoCardUI;

  // Se pode editar, envolve a UI com o Popover
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild disabled={isLoading}>
        <button type="button" className="w-full appearance-none text-left">
          {PrazoCardUI}
        </button>
      </PopoverTrigger>
      <PopoverContent className="pointer-events-auto w-auto p-0" align="center">
        <Calendar
          mode="single"
          selected={prazo}
          onSelect={handleSelect}
          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
          initialFocus
        />
        {/* Adicionei a opção de Remover o prazo! */}
        <div className="border-t bg-muted/20 p-3">
          <Button
            variant="destructive"
            className="h-8 w-full text-xs"
            onClick={() => handleSelect(undefined)}
          >
            <X className="mr-2 h-3 w-3" /> Remover Prazo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Linha de apoio do card de prazo: "Faltam X dias", "Vence hoje", "Venceu há X dias". */
function describePrazo(prazo: Date, hoje: Date, isFechada: boolean): string {
  if (isFechada) return "Prazo encerrado";

  const soData = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dias = differenceInDays(soData(prazo), soData(hoje));

  if (dias > 1) return `Faltam ${dias} dias`;
  if (dias === 1) return "Vence amanhã";
  if (dias === 0) return "Vence hoje";
  if (dias === -1) return "Venceu ontem";
  return `Venceu há ${Math.abs(dias)} dias`;
}
